/**
 * Size Guide Modal - mdlondon Design System
 * Handles size guide modal display, tabs, and unit conversion
 */

class SizeGuideModal {
  constructor() {
    this.modal = null;
    this.currentUnit = 'cm';
    this.init();
  }

  init() {
    this.createModal();
    this.setupEventListeners();
  }

  createModal() {
    const modal = document.createElement('div');
    modal.className = 'size-guide-modal';
    modal.innerHTML = '<div class="size-guide-modal__content">' +
      '<button class="size-guide-modal__close" aria-label="Close size guide">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor">' +
          '<line x1="18" y1="6" x2="6" y2="18"></line>' +
          '<line x1="6" y1="6" x2="18" y2="18"></line>' +
        '</svg>' +
      '</button>' +
      '<div class="size-guide"></div>' +
    '</div>';

    document.body.appendChild(modal);
    this.modal = modal;
  }

  setupEventListeners() {
    // Size guide link clicks
    document.addEventListener('click', (e) => {
      const link = e.target.closest('.size-guide-link, .product__size-guide-link');
      if (link) {
        e.preventDefault();
        const category = link.dataset.category || 'clothing';
        this.open(category);
      }
    });

    // Close button
    this.modal.addEventListener('click', (e) => {
      if (e.target.matches('.size-guide-modal__close') || e.target.closest('.size-guide-modal__close')) {
        this.close();
      }
    });

    // Close on overlay click
    this.modal.addEventListener('click', (e) => {
      if (e.target.classList.contains('size-guide-modal')) {
        this.close();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('size-guide-modal--active')) {
        this.close();
      }
    });

    // Tab switching
    this.modal.addEventListener('click', (e) => {
      const tab = e.target.closest('.size-guide__tab');
      if (tab) {
        this.switchTab(tab);
      }
    });

    // Unit toggle
    this.modal.addEventListener('click', (e) => {
      const unitButton = e.target.closest('.size-guide__unit-button');
      if (unitButton) {
        this.switchUnit(unitButton.dataset.unit);
      }
    });
  }

  open(category = 'clothing') {
    this.renderContent(category);
    this.modal.classList.add('size-guide-modal--active');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.modal.classList.remove('size-guide-modal--active');
    document.body.style.overflow = '';
  }

  renderContent(category) {
    const container = this.modal.querySelector('.size-guide');
    container.innerHTML = '<div class="size-guide__header">' +
      '<h2 class="size-guide__title">Size Guide</h2>' +
      '<p class="size-guide__subtitle">Find your perfect fit</p>' +
    '</div>' +
    this.renderTabs(category) +
    '<div class="size-guide__content">' +
      this.renderUnitsToggle() +
      '<div class="size-guide__section size-guide__section--active" data-tab="clothing">' +
        this.renderClothingSizes() +
      '</div>' +
      '<div class="size-guide__section" data-tab="shoes">' +
        this.renderShoeSizes() +
      '</div>' +
      '<div class="size-guide__section" data-tab="accessories">' +
        this.renderAccessorySizes() +
      '</div>' +
      this.renderHowToMeasure() +
      this.renderFitGuide() +
      this.renderContact() +
    '</div>';
  }

  renderTabs(activeTab) {
    const tabs = [
      { id: 'clothing', label: 'Clothing' },
      { id: 'shoes', label: 'Shoes' },
      { id: 'accessories', label: 'Accessories' }
    ];

    return '<div class="size-guide__tabs">' +
      tabs.map(tab =>
        '<button class="size-guide__tab' +
        (tab.id === activeTab ? ' size-guide__tab--active' : '') +
        '" data-tab="' + tab.id + '">' + tab.label + '</button>'
      ).join('') +
    '</div>';
  }

  renderUnitsToggle() {
    return '<div class="size-guide__units">' +
      '<span class="size-guide__units-label">Measurements:</span>' +
      '<div class="size-guide__units-toggle">' +
        '<button class="size-guide__unit-button size-guide__unit-button--active" data-unit="cm">CM</button>' +
        '<button class="size-guide__unit-button" data-unit="inches">INCHES</button>' +
      '</div>' +
    '</div>';
  }

  renderClothingSizes() {
    const sizes = [
      { name: 'XS', chest: { cm: '86-91', inches: '34-36' }, waist: { cm: '71-76', inches: '28-30' }, hips: { cm: '91-96', inches: '36-38' } },
      { name: 'S', chest: { cm: '91-96', inches: '36-38' }, waist: { cm: '76-81', inches: '30-32' }, hips: { cm: '96-101', inches: '38-40' } },
      { name: 'M', chest: { cm: '96-101', inches: '38-40' }, waist: { cm: '81-86', inches: '32-34' }, hips: { cm: '101-106', inches: '40-42' } },
      { name: 'L', chest: { cm: '101-106', inches: '40-42' }, waist: { cm: '86-91', inches: '34-36' }, hips: { cm: '106-111', inches: '42-44' } },
      { name: 'XL', chest: { cm: '106-111', inches: '42-44' }, waist: { cm: '91-96', inches: '36-38' }, hips: { cm: '111-116', inches: '44-46' } }
    ];

    return '<table class="size-chart">' +
      '<thead>' +
        '<tr>' +
          '<th>Size</th>' +
          '<th>Chest</th>' +
          '<th>Waist</th>' +
          '<th>Hips</th>' +
        '</tr>' +
      '</thead>' +
      '<tbody>' +
        sizes.map(size =>
          '<tr>' +
            '<td class="size-chart__size-name">' + size.name + '</td>' +
            '<td data-cm="' + size.chest.cm + '" data-inches="' + size.chest.inches + '">' + size.chest.cm + '</td>' +
            '<td data-cm="' + size.waist.cm + '" data-inches="' + size.waist.inches + '">' + size.waist.cm + '</td>' +
            '<td data-cm="' + size.hips.cm + '" data-inches="' + size.hips.inches + '">' + size.hips.cm + '</td>' +
          '</tr>'
        ).join('') +
      '</tbody>' +
    '</table>';
  }

  renderShoeSizes() {
    const sizes = [
      { uk: '3', us: '5', eu: '36' },
      { uk: '4', us: '6', eu: '37' },
      { uk: '5', us: '7', eu: '38' },
      { uk: '6', us: '8', eu: '39' },
      { uk: '7', us: '9', eu: '40' },
      { uk: '8', us: '10', eu: '41' },
      { uk: '9', us: '11', eu: '42' },
      { uk: '10', us: '12', eu: '43' }
    ];

    return '<table class="size-chart">' +
      '<thead>' +
        '<tr>' +
          '<th>UK</th>' +
          '<th>US</th>' +
          '<th>EU</th>' +
        '</tr>' +
      '</thead>' +
      '<tbody>' +
        sizes.map(size =>
          '<tr>' +
            '<td class="size-chart__size-name">' + size.uk + '</td>' +
            '<td>' + size.us + '</td>' +
            '<td>' + size.eu + '</td>' +
          '</tr>'
        ).join('') +
      '</tbody>' +
    '</table>';
  }

  renderAccessorySizes() {
    return '<div class="size-guide__accessory-info">' +
      '<p>Our accessories are designed to fit most body types. Specific measurements are provided on each product page.</p>' +
    '</div>';
  }

  renderHowToMeasure() {
    return '<div class="size-guide__how-to-measure">' +
      '<h3 class="size-guide__how-to-measure-title">How to Measure</h3>' +
      '<ul class="size-guide__measurement-list">' +
        '<li class="size-guide__measurement-item">' +
          '<div class="size-guide__measurement-number">1</div>' +
          '<div class="size-guide__measurement-text"><strong>Chest:</strong> Measure around the fullest part of your chest, keeping the tape horizontal.</div>' +
        '</li>' +
        '<li class="size-guide__measurement-item">' +
          '<div class="size-guide__measurement-number">2</div>' +
          '<div class="size-guide__measurement-text"><strong>Waist:</strong> Measure around your natural waistline, keeping the tape comfortably loose.</div>' +
        '</li>' +
        '<li class="size-guide__measurement-item">' +
          '<div class="size-guide__measurement-number">3</div>' +
          '<div class="size-guide__measurement-text"><strong>Hips:</strong> Measure around the fullest part of your hips, about 20cm below your waist.</div>' +
        '</li>' +
      '</ul>' +
    '</div>';
  }

  renderFitGuide() {
    return '<div class="size-guide__fit-guide">' +
      '<h3 class="size-guide__fit-guide-title">Fit Guide</h3>' +
      '<div class="size-guide__fit-options">' +
        '<div class="size-guide__fit-option">' +
          '<div class="size-guide__fit-icon">👔</div>' +
          '<h4 class="size-guide__fit-name">Slim Fit</h4>' +
          '<p class="size-guide__fit-description">Close to body, tailored silhouette</p>' +
        '</div>' +
        '<div class="size-guide__fit-option">' +
          '<div class="size-guide__fit-icon">👕</div>' +
          '<h4 class="size-guide__fit-name">Regular Fit</h4>' +
          '<p class="size-guide__fit-description">Classic fit with room to move</p>' +
        '</div>' +
        '<div class="size-guide__fit-option">' +
          '<div class="size-guide__fit-icon">🧥</div>' +
          '<h4 class="size-guide__fit-name">Relaxed Fit</h4>' +
          '<p class="size-guide__fit-description">Loose, comfortable fit</p>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  renderContact() {
    return '<div class="size-guide__contact">' +
      '<p class="size-guide__contact-text">Need help finding your size?</p>' +
      '<a href="/pages/contact" class="size-guide__contact-link">Contact Our Support Team</a>' +
    '</div>';
  }

  switchTab(tab) {
    const tabId = tab.dataset.tab;

    // Update tab buttons
    this.modal.querySelectorAll('.size-guide__tab').forEach(t => {
      t.classList.remove('size-guide__tab--active');
    });
    tab.classList.add('size-guide__tab--active');

    // Update sections
    this.modal.querySelectorAll('.size-guide__section').forEach(section => {
      section.classList.remove('size-guide__section--active');
    });
    const section = this.modal.querySelector('.size-guide__section[data-tab="' + tabId + '"]');
    if (section) {
      section.classList.add('size-guide__section--active');
    }
  }

  switchUnit(unit) {
    this.currentUnit = unit;

    // Update button states
    this.modal.querySelectorAll('.size-guide__unit-button').forEach(btn => {
      btn.classList.toggle('size-guide__unit-button--active', btn.dataset.unit === unit);
    });

    // Update table values
    this.modal.querySelectorAll('td[data-cm][data-inches]').forEach(cell => {
      const value = unit === 'cm' ? cell.dataset.cm : cell.dataset.inches;
      cell.textContent = value;
    });
  }
}

// Initialize size guide modal
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SizeGuideModal();
  });
} else {
  new SizeGuideModal();
}
